import {
  SchoolAttributes,
  SchoolDetailsType,
  UniversityAttributes,
  UniversityDetailsType,
} from 'hooks/useCandidateProfile';
import { TCandidate } from 'lib/models/User.model';
import { EducationHistoryAttributes } from 'lib/models/candidate';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';

export enum EDUCATION_TYPE {
  University = 'University',
  School = 'School',
}

export const getUpdateUniversityValue = async (info: UniversityDetailsType) => {
  const {
    id,
    isNew,
    university,
    degree,
    subject,
    start_date,
    end_date,
    description,
    study_year,
    degree_name,
    location,
    currently_studying,
    graduation_year,
  } = info;
  const candidate: {
    graduation_year?: number;
    education_histories_attributes: UniversityAttributes[];
  } = {
    graduation_year: graduation_year ? graduation_year : undefined,
    education_histories_attributes: [
      {
        id: isNew ? '' : id,
        start_date,
        end_date,
        description: !Array.isArray(description)
          ? description
              ?.split('\n')
              .map((item) => item.replace(/•/g, '').trim())
          : description,
        ...(university.id && { university_id: university.id }),
        ...(!university.id && { university_name: university.name }),
        degree_id: degree.id,
        subject_id: subject.id,
        subject_name: subject.name,
        education_type: EDUCATION_TYPE.University.toLowerCase(),
        currently_studying: currently_studying,
        current_study_year: study_year ? Number(study_year) : null,
        ...(degree_name && { degree_name }),
        location,
      },
    ],
  };
  return candidate;
};

export const getUpdateSchoolValue = async (info: SchoolDetailsType) => {
  const {
    id,
    isNew,
    school_name,
    school_type,
    start_date,
    end_date,
    description,
    location,
  } = info;
  const candidate: {
    education_histories_attributes: SchoolAttributes[];
  } = {
    education_histories_attributes: [
      {
        id: isNew ? '' : id,
        start_date,
        end_date,
        description: !Array.isArray(description)
          ? description
              ?.split('\n')
              .map((item) => item.replace(/•/g, '').trim())
          : description,
        school_name,
        school_type,
        education_type: EDUCATION_TYPE.School.toLowerCase(),
        location,
      },
    ],
  };
  return candidate;
};

export const getUniversityInfo = (candidate: TCandidate) => {
  const educationHistories = parseArrayResponse<EducationHistoryAttributes>(
    candidate.education_histories
  );
  const uniDetails = educationHistories
    .filter(
      (item) => item.education_type === EDUCATION_TYPE.University.toLowerCase()
    )
    .map((info) => {
      const {
        id,
        start_date,
        end_date,
        description,
        current_study_year,
        university: universityResponse,
        degree: degreeResponse,
        subject: subjectsResponse,
        degree_name,
        university_id,
        university_name,
        location,
        currently_studying,
      } = info;
      const university =
        parseResponse<{ id: string; name: string }>(universityResponse);
      const degree = parseResponse(degreeResponse);
      const subject = parseResponse(subjectsResponse);
      return {
        id,
        type: EDUCATION_TYPE.University,
        university: {
          id: university?.id || university_id,
          name: university?.name || university_name || '',
        },
        degree: {
          id: degree?.id,
          name: degree?.name,
        },
        subject: {
          id: subject?.id,
          name: subject?.name,
        },
        study_year: current_study_year ? String(current_study_year) : null,
        start_date,
        end_date,
        currently_studying: currently_studying,
        description: (description as string[])
          ?.map((pointer) => (pointer ? `• ${pointer}` : ''))
          .join('\n'),

        isNew: false,
        degree_name,
        location,
      };
    });
  return uniDetails;
};

export const getSchoolInfo = (candidate: TCandidate) => {
  const educationHistories = parseArrayResponse<EducationHistoryAttributes>(
    candidate.education_histories
  );
  const schoolDetails = educationHistories
    .filter(
      (item) => item.education_type === EDUCATION_TYPE.School.toLowerCase()
    )
    .map((info) => {
      const {
        id,
        school_name,
        school_type,
        start_date,
        end_date,
        description,
        location,
      } = info;

      return {
        id,
        type: EDUCATION_TYPE.School,
        school_name,
        school_type,
        start_date,
        end_date,
        description: (description as string[])
          ?.map((pointer) => (pointer ? `• ${pointer}` : ''))
          .join('\n'),
        location,
        isNew: false,
      };
    });
  return schoolDetails;
};
